import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import { formatTimeSaudi } from '../../utils/saudiTime';
import SalesPaymentReceivedModal from './SalesPaymentReceivedModal';

export default function SalesUnpaidOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [paymentModalOrder, setPaymentModalOrder] = useState(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  const load = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await apiService.orders.getSalesUnpaidOrders();
      const rows = Array.isArray(res.data) ? res.data : [];
      setOrders(rows.filter((o) => Boolean(o.delivered) && !o.paymentReceived));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load unpaid orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const submitPaymentReceived = async (orderId, amountCollected) => {
    setPaymentSubmitting(true);
    setError('');
    try {
      await apiService.orders.markSalesOrderPaymentReceived(orderId, { amountCollected });
      setPaymentModalOrder(null);
      await load();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to confirm payment received';
      setError(msg);
      throw err;
    } finally {
      setPaymentSubmitting(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const slotOptions = useMemo(() => {
    const set = new Set();
    orders.forEach((o) => {
      const s = String(o.deliverySlot || '').trim();
      if (s) set.add(s);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const filtered = selectedSlot ? orders.filter((o) => (o.deliverySlot || '') === selectedSlot) : orders;

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-amber-500 p-3 sm:p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-700">Unpaid orders</h1>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">{error}</div>
        )}

        {!loading && slotOptions.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="slot-filter">
              Filter by delivery slot
            </label>
            <select
              id="slot-filter"
              value={selectedSlot}
              onChange={(e) => setSelectedSlot(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-700 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All slots</option>
              {slotOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        )}

        {loading && <div className="py-8 text-center text-gray-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="py-8 text-center text-gray-500 border border-gray-200 rounded-lg">No unpaid orders.</div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="space-y-4">
            {filtered.map((o) => {
              const itemCount = (o.items || []).reduce((sum, i) => sum + (i.orderQty || 0), 0);
              return (
                <div key={o._id} className="border border-gray-200 rounded-lg p-3 sm:p-4 border-l-4 border-l-amber-400">
                  <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-800 text-base sm:text-lg">{o.orderNo}</div>
                      <div className="text-base text-gray-500 mt-1">
                        {formatTimeSaudi(o.dateTime)} · {itemCount} item(s)
                      </div>
                      {(o.customerName || o.customerPhone) && (
                        <div className="text-sm text-gray-700 mt-2">
                          {o.customerName ? <div>Customer: <span className="font-medium">{o.customerName}</span></div> : null}
                          {o.customerPhone ? <div>Phone: <span className="font-medium">{o.customerPhone}</span></div> : null}
                        </div>
                      )}
                      {o.deliverySlot && (
                        <div className="mt-2 text-sm text-gray-700">
                          Slot: <span className="font-semibold text-blue-700">{o.deliverySlot}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      <span className="inline-block px-3 py-1.5 rounded-full text-sm font-bold bg-blue-100 text-blue-800 border border-blue-200">
                        ✓ Delivered
                      </span>
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold bg-red-100 text-red-800 border border-red-200">
                        <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-500" />
                        Payment pending
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setError('');
                          setPaymentModalOrder(o);
                        }}
                        disabled={paymentSubmitting}
                        className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium py-2 px-3 rounded-lg min-h-[40px] touch-manipulation"
                      >
                        Payment received
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <SalesPaymentReceivedModal
          open={Boolean(paymentModalOrder)}
          order={paymentModalOrder}
          onClose={() => !paymentSubmitting && setPaymentModalOrder(null)}
          onSubmit={submitPaymentReceived}
          submitting={paymentSubmitting}
        />
      </div>
    </div>
  );
}

